import { Spacing } from '@persona-id/design-tokens';
import { FC, MouseEvent, PropsWithChildren, useMemo } from 'react';
import {
  Payload as LegendPayload,
  Props as LegendProps,
} from 'recharts/types/component/DefaultLegendContent';
import styled, { css } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Heading, Label } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';

// Recharts styles -- individual components cannot be wrapped with styled
const ChartLayout = styled.div`
  /* All overrides are for legend ATM. Good candidate for extraction to custom component. */
  /* Legend dots */
  path.recharts-symbols {
    transition: fill ${TransitionDuration.Fast};
  }

  path.recharts-rectangle {
    cursor: pointer;
  }
  /* Legend left padding to avoid hugging bars -- bizarre that Recharts forces margin: 0 */
  .recharts-default-legend {
    display: flex;
    flex-direction: column-reverse;
    margin-left: ${Spacing[4]} !important;
  }

  .recharts-legend-item {
    cursor: pointer;
    margin: 0 !important;
    padding: ${Spacing[1]} ${Spacing[2]};
    transition-duration: ${TransitionDuration.Fast};
    border-radius: ${({ theme }) => theme.element.borderRadius};
    display: flex !important;
    align-items: center;

    &:hover {
      box-shadow:
        0 0 0 3px transparent,
        ${Elevation.Mid};
    }

    &:active {
      background: ${({ theme }) => theme.color.background.secondary};
    }

    &.inactive > span {
      color: ${({ theme }) => theme.color.text.disabled};
    }
  }

  .recharts-legend-item-text {
    color: ${({ theme }) => theme.color.text.secondary};
  }

  .recharts-tooltip-wrapper {
    z-index: ${Z_INDICES.tooltip};
  }

  .recharts-tooltip-cursor {
    fill: ${({ theme }) => theme.color.background.secondary};
    opacity: 0.5;
  }
`;

export default ChartLayout;

export const ChartTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 400px;
  overflow-y: scroll;

  box-shadow: ${Elevation.Mid};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${Spacing[2]} ${Spacing[3]};
  z-index: ${Z_INDICES.toast};
  background-color: ${({ theme }) => theme.color.primary.white};
`;

const HeaderContainer = styled.div`
  padding: ${Spacing[2]};
  padding-top: ${Spacing[1]};
  width: 100%;
`;

export const ChartTooltipDivider = styled.div`
  margin-top: ${Spacing[5]};
  padding-top: ${Spacing[1]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  width: 100%;
`;

export const ChartTooltipHeader: FC<PropsWithChildren<object>> = ({ children }) => (
  <HeaderContainer>
    <Heading size='sm'>{children}</Heading>
  </HeaderContainer>
);

interface ActivatableProps {
  active?: boolean;
  hasActive?: boolean;
}

export const ChartTooltipLabelContainer = styled.div<ActivatableProps>`
  display: grid;
  grid-template-columns: 3fr 2fr 2fr;
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} ${Spacing[4]} ${Spacing[1]} ${Spacing[2]};
  width: 100%;
  min-width: 260px;

  > :not(:first-child) {
    justify-self: end;
  }

  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ active, hasActive }) => (hasActive ? (active ? 1 : 0.3) : 1)};
`;

export const ChartTooltipLabel = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  color: ${({ theme }) => theme.color.text.secondary};
  cursor: inherit;
`;

// Overwrite mouse handlers since Recharts types are wrong
interface ChartLegendContentProps<TValue>
  extends Omit<LegendProps, 'onMouseEnter' | 'onMouseLeave' | 'onClick'> {
  active?: TValue[];
  height?: number;
  onMouseEnter?: (entry: LegendPayload, i: number, e: MouseEvent) => void;
  onMouseLeave?: (entry: LegendPayload, i: number, e: MouseEvent) => void;
  onClick?: (entry: LegendPayload, i: number, e: MouseEvent) => void;
}

export const ChartLegendContent = <TValue,>({
  // own props
  active = [],
  // recharts injected props
  layout,
  payload,
  formatter,
  onMouseEnter,
  onMouseLeave,
  onClick,
  inactiveColor,
  height,
}: ChartLegendContentProps<TValue>) => {
  const items = useMemo(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return payload.map((entry, i) => {
      if (entry == null) {
        return;
      }

      if (entry.type === 'none') {
        return null;
      }
      const inactive = entry.inactive || (active.length > 0 && !active.includes(entry.value));

      const color = inactive ? inactiveColor : entry.color;

      return (
        <li
          className={`recharts-legend-item legend-item-${i} ${inactive && 'inactive'}`}
          id={`legend-item-${i}`}
          key={`legend-item-${i}`}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onMouseEnter={(e) => onMouseEnter(entry, i, e)}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onMouseLeave={(e) => onMouseLeave(entry, i, e)}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onClick={(e) => onClick(entry, i, e)}
        >
          <ChartLegendLabelContainer>
            <ChartLegendLabel>
              <StyledDot style={{ color }} />
              <ChartLegendLabelText
                data-test='chart-legend-label-text'
                active={!inactive}
                hasActive={true}
              >
                {formatter ? formatter(entry.value, entry, i) : entry.value}
              </ChartLegendLabelText>
            </ChartLegendLabel>
          </ChartLegendLabelContainer>
        </li>
      );
    });
  }, [payload, formatter]);

  if (!payload || !payload.length) {
    return null;
  }

  return (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <StyledChartList className='recharts-default-legend' layout={layout} height={height}>
      {items}
    </StyledChartList>
  );
};

export const ChartLegendLabelContainer = styled.div`
  padding: ${Spacing[1]} ${Spacing[4]} ${Spacing[1]} ${Spacing[2]};
  width: 100%;
`;

export const ChartLegendLabel = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  color: ${({ theme }) => theme.color.text.secondary};
`;

export const ChartLegendLabelText = styled.span<ActivatableProps>`
  opacity: ${({ active, hasActive }) => (hasActive ? (active ? 1 : 0.3) : 1)};
  transition: opacity ${TransitionDuration.Fast};
`;

const StyledChartList = styled.ul<{ layout: string; height?: number }>`
  ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return css`
          flex-direction: row !important;
          justify-content: center;
        `;
    }
  }};

  ${({ height }) => {
    if (height) {
      return css`
        height: ${height}px;
        overflow-y: scroll;
      `;
    }
  }};
`;

const StyledDot = styled(Dot)`
  margin-right: ${Spacing[2]};
  transition: color ${TransitionDuration.Fast};
`;
